import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faSearch,
  faSquareCheck,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { DialogCloseResult, DialogResult } from '@progress/kendo-angular-dialog';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { NotificationService } from '@progress/kendo-angular-notification';
import { searchIcon, SVGIcon } from '@progress/kendo-svg-icons';
import _, { orderBy } from 'lodash';
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { LoadingService } from 'src/app/services/loading.service';
import { AbstractComponent } from 'src/app/shared/components/abstract-component';
import { dialogTimer } from 'src/app/shared/constants/dialog-specs';
import {
  allNotificationsPageSize,
  NotificationAction,
  NotificationReadAllBit,
} from 'src/app/shared/enums/notification';
import { formatNotificationDateTime } from 'src/app/shared/functions/format-notifications-datetime';
import { AllNotificationsResponse } from '../../interface/get-all-notifications-response';
import { MarkPayload } from '../../interface/mark-payload';
import { DashboardService } from '../../services/dashboard.service';
import { MarkingWarnService } from '../../services/marking-warn.service';
import { MarkReadNotificationService } from '../../services/notification.service';
import {
  ICON_HEIGHT_INDEX,
  ICON_SVG_PATH_INDEX,
  ICON_WIDTH_INDEX,
  SEARCH_DEBOUNCE_TIME_MS,
} from 'src/app/shared/constants/constants';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  standalone: false,
})
export class NotificationComponent extends AbstractComponent implements OnInit {
  squareCheck: SVGIcon = this.customFaIcon(faSquareCheck as IconDefinition);
  readonly defaultTotal: number = 0;
  constructor(
    library: FaIconLibrary,
    private dashboardService: DashboardService,
    private notificationService: NotificationService,
    public loadingService: LoadingService,
    private markReadNotificationService: MarkReadNotificationService,
    private markingWarnService: MarkingWarnService,
    private vcr: ViewContainerRef
  ) {
    super();
    library.addIcons(faSearch);
    this.loadingService.show();
  }
  public searchIcon: SVGIcon = searchIcon;
  public pageSize: number = allNotificationsPageSize;
  public gridView: GridDataResult;
  public searchValue: string = '';
  private searchInput$: Subject<string> = new Subject<string>();
  public allNotifications: AllNotificationsResponse[] = [];
  skip: number = 0;
  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,
      content: `<path d="${icon.icon[ICON_SVG_PATH_INDEX]}" />`,
      viewBox: `0 0 ${icon.icon[ICON_WIDTH_INDEX]} ${icon.icon[ICON_HEIGHT_INDEX]}`,
    };
  }

  ngOnInit(): void {
    this.fetchAllNotifications();
    // Once a notification is marked as read, start polling again to refresh the list.
    this.searchInput$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_TIME_MS), // wait 300ms after typing stops
        distinctUntilChanged(), // only fire if value changed
        takeUntil(this.componentDestroyed)
      )
      .subscribe((value: string) => {
        this.searchValue = value?.trim() || '';
        this.fetchAllNotifications(this.searchValue);
      });
    this.markReadNotificationService.notificationMarked$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((isView: boolean) => {
        if (!isView) this.fetchAllNotifications(this.searchValue);
      });
  }

  fetchAllNotifications(nycId: string = ''): void {
    const pageNumber: number = Math.floor(this.skip / this.pageSize);
    this.dashboardService
      .getAllNotifications(pageNumber, this.pageSize, nycId)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe({
        next: (response: AllNotificationsResponse[]) => {
          this.loadingService.hide();
          this.allNotifications = orderBy(
            response || [],
            ['dateTime'],
            ['desc']
          );

          // Format dateTime for each notification item
          response.forEach((item: AllNotificationsResponse) => {
            item.dateTime = formatNotificationDateTime(
              new Date(item.dateTime), // Date/time from the backend
              new Date() // todays Date
            );
          });
          this.gridView = {
            data: this.allNotifications,
            total: _.first(response)?.totalCount ?? this.defaultTotal,
          };
        },
        error: () => {
          this.loadingService.hide();
          this.notificationService.show({
            content: 'Fetching notifications failed. Please try again later.',
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  markAllAsDoneOrRead(): void {
    // Payload is built for future API enhancements
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const markingPayload: MarkPayload[] = this.allNotifications.map(
      (notification: AllNotificationsResponse) => ({
        notificationSk: notification.notificationSk,
        studentSk: notification.studentSK,
        notificationTypeId: notification.notificationTypeId,
        notificationStatus: NotificationAction.Read,
        markAllRead: NotificationReadAllBit.All,
        authUserRoleLocationSk: notification.authUserRoleLocationSk,
      })
    );
    this.loadingService.show();
    this.dashboardService
      .markAllRead()
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe({
        next: () => {
          this.loadingService.hide();
          this.markReadNotificationService.notifyNotificationMarked();
          this.fetchAllNotifications();
          this.notificationService.show({
            content: 'Marking notifications successful.',
            cssClass: 'notification-success',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'success', icon: true },
          });
        },
        error: () => {
          this.loadingService.hide();
          this.notificationService.show({
            content: 'Marking notifications failed. Please try again later.',
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  onPageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.fetchAllNotifications(this.searchValue); // fetch new page from backend
  }

  public handleNotificationSearch(id: string): void {
    // this.searchValue = id;
    // this.fetchAllNotifications(id);
    this.searchInput$.next(id?.trim());
  }

  override afterOnDestroy(): void {
    // Additional cleanup if needed
  }
  showAllReadWarning(): void {
    this.markingWarnService
      .open('markAll', this.vcr)
       
      .pipe(take(1))
      .subscribe((result: DialogCloseResult | DialogResult) => {
        if (!(result instanceof DialogCloseResult)) {
          this.markAllAsDoneOrRead();
        }
      });
  }
}
